import type {CallIntent} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import logger from '../../../services/logger';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {loadDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {resolveMatrixUserIds} from '../../groupCalls/services/ringClient';
import {isRoomMember} from '../group/addPeople';

export type TeamCallOutcome = 'started' | 'busy' | 'failed';

const JOIN_WAIT_MS = 10_000;
const JOIN_POLL_MS = 250;

const waitForMembers = async (roomId: string, matrixUserIds: readonly string[]): Promise<readonly string[]> => {
    const deadline = Date.now() + JOIN_WAIT_MS;
    while (Date.now() < deadline) {
        if (matrixUserIds.every(userId => isRoomMember(roomId, userId))) {
            break;
        }
        await new Promise(resolve => window.setTimeout(resolve, JOIN_POLL_MS));
    }
    return matrixUserIds.filter(userId => isRoomMember(roomId, userId));
};

export const startTeamGroupCall = async (userIds: readonly number[], intent: CallIntent): Promise<TeamCallOutcome> => {
    try {
        const resolved = await resolveMatrixUserIds(userIds);
        const matrixUserIds = userIds.map(userId => resolved.get(userId)).filter((value): value is string => !!value);
        if (matrixUserIds.length === 0) {
            return 'failed';
        }
        const roomId = matrixService.findUnnamedGroupRoom([...matrixUserIds], true) ?? await matrixService.createRoom({invite: [...matrixUserIds]});
        const joined = await waitForMembers(roomId, matrixUserIds);
        const opened = await groupCallService.openLobby(roomId, intent);
        if (!opened) {
            return 'busy';
        }
        await groupCallService.join({
            intent,
            microphoneEnabled: true,
            cameraEnabled: intent === 'video',
            devices: loadDevicePreferences(),
            targets: joined.length > 0 ? joined : matrixUserIds,
        });
        logger.info('Team group call started', {source: 'group_call', event: 'team_call_started', roomId, people: matrixUserIds.length, joined: joined.length});
        return 'started';
    } catch (error) {
        logger.warn('Team group call failed', {
            source: 'group_call',
            event: 'team_call_failed',
            error: error instanceof Error ? error.message : String(error),
        });
        return 'failed';
    }
};
